import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import Modal from "../../../components/modal/Modal";
import { InfectionControlChecklist, InfectionControlChecklistItem } from "../../../types/api";
import { useProjectUtilityStore } from "../../../store/utilitySystemsStore";
import { useInfectionControlStore } from "../../../store/utilitySystemsStore";
import { useLocationStore } from "../../../store/masterDataStore";

interface InfectionControlChecklistFormProps {
  isOpen: boolean;
  onClose: () => void;
  checklist?: InfectionControlChecklist | null;
  mode: "create" | "edit" | "view";
  onSuccess?: () => void;
}

const DEFAULT_CHECKLIST_ITEMS = [
  "Pre-construction infection control risk assessment completed",
  "Isolation barriers installed and verified",
  "Negative pressure system tested and operational",
  "HEPA filtration system installed and certified",
  "Air quality monitoring plan established",
  "Waste disposal procedures documented",
  "Personal protective equipment (PPE) requirements defined",
  "Construction personnel training on infection control completed",
  "Daily inspection schedule established",
  "Emergency response plan for infection control incidents documented",
];

const InfectionControlChecklistForm: React.FC<InfectionControlChecklistFormProps> = ({
  isOpen,
  onClose,
  checklist,
  mode,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    projectId: "",
    locationId: "",
    checklistItems: [] as InfectionControlChecklistItem[],
    remarks: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { projects, fetchProjects } = useProjectUtilityStore();
  const { allLocations, fetchAllLocations } = useLocationStore();
  const {
    checklistsLoading,
    fetchChecklistById,
    createChecklist,
    updateChecklist,
  } = useInfectionControlStore();

  const isDisabled = mode === "view" || checklistsLoading;
  const isReadOnly = mode === "view";

  const loadChecklistData = useCallback(async () => {
    if (!checklist) return;

    try {
      const id = checklist.id || checklist._id;
      if (!id) return;

      const data = await fetchChecklistById(id);
      if (data) {
        setFormData({
          projectId: data.projectId || "",
          locationId: data.locationId || "",
          checklistItems: data.checklistItems || [],
          remarks: data.remarks || "",
        });
      } else {
        setFormData({
          projectId: checklist.projectId || "",
          locationId: checklist.locationId || "",
          checklistItems: checklist.checklistItems || [],
          remarks: checklist.remarks || "",
        });
      }
    } catch (error) {
      console.error("Error loading checklist:", error);
      toast.error("Failed to load checklist data");
      if (checklist) {
        setFormData({
          projectId: checklist.projectId || "",
          locationId: checklist.locationId || "",
          checklistItems: checklist.checklistItems || [],
          remarks: checklist.remarks || "",
        });
      }
    }
  }, [checklist, fetchChecklistById]);

  useEffect(() => {
    if (isOpen) {
      fetchProjects();
      fetchAllLocations();
      if (mode === "edit" || mode === "view") {
        loadChecklistData();
      } else {
        setFormData({
          projectId: "",
          locationId: "",
          checklistItems: DEFAULT_CHECKLIST_ITEMS.map((item) => ({
            item,
            checked: false,
            remarks: "",
          })),
          remarks: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, mode, fetchProjects, fetchAllLocations, loadChecklistData]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleChecklistItemChange = (index: number, field: keyof InfectionControlChecklistItem, value: any) => {
    setFormData((prev) => {
      const newItems = [...prev.checklistItems];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, checklistItems: newItems };
    });
  };

  const handleAddChecklistItem = () => {
    setFormData((prev) => ({
        ...prev,
        checklistItems: [
          ...prev.checklistItems,
          { item: "", checked: false, remarks: "" } as InfectionControlChecklistItem,
        ],
    }));
  };

  const handleRemoveChecklistItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      checklistItems: prev.checklistItems.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.projectId) {
      newErrors.projectId = "Project is required";
    }

    if (!formData.locationId) {
      newErrors.locationId = "Location is required";
    }

    if (!formData.checklistItems || formData.checklistItems.length === 0) {
      newErrors.checklistItems = "At least one checklist item is required";
    } else {
      formData.checklistItems.forEach((item, index) => {
        if (!item.item.trim()) {
          newErrors[`checklistItem_${index}`] = "Checklist item text is required";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isReadOnly || checklistsLoading) return;

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      const submitData = {
        projectId: formData.projectId,
        locationId: formData.locationId,
        checklistItems: formData.checklistItems,
        remarks: formData.remarks.trim() || undefined,
      };

      if (mode === "create") {
        await createChecklist(submitData);
        toast.success("Infection control checklist created successfully");
      } else if (mode === "edit" && checklist) {
        const id = checklist.id || checklist._id;
        if (!id) {
          toast.error("Checklist ID not found");
          return;
        }
        await updateChecklist(id, submitData);
        toast.success("Infection control checklist updated successfully");
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.resMessage ||
          error.message ||
          `Failed to ${mode === "create" ? "create" : "update"} checklist`
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeButton={false}
      modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
    >
      <div className="pt-6 px-6 flex-1 overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
          {mode === "create"
            ? "Create Infection Control Checklist"
            : mode === "edit"
            ? "Edit Infection Control Checklist"
            : "View Infection Control Checklist"}
        </h2>
        <form onSubmit={handleSubmit} id="infectionControlChecklistForm" className="flex flex-col gap-6 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Project <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.projectId}
                onChange={(e) => handleChange("projectId", e.target.value)}
                disabled={isDisabled}
                className={`w-full p-2 border ${
                  errors.projectId ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
              >
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project.id || project._id} value={project.id || project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
              {errors.projectId && (
                <p className="text-red-500 text-sm mt-1">{errors.projectId}</p>
              )}
            </div>

            {/* Location */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Location <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.locationId}
                onChange={(e) => handleChange("locationId", e.target.value)}
                disabled={isDisabled}
                className={`w-full p-2 border ${
                  errors.locationId ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
              >
                <option value="">Select Location</option>
                {allLocations.map((location) => (
                  <option key={location._id} value={location._id}>
                    {location.room} - {location.floorNumber}
                  </option>
                ))}
              </select>
              {errors.locationId && (
                <p className="text-red-500 text-sm mt-1">{errors.locationId}</p>
              )}
            </div>
          </div>

          {/* Checklist Items */}
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-gray-700">
                Checklist Items <span className="text-red-500">*</span>
              </label>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={handleAddChecklistItem}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-[#00A8A8] text-white rounded hover:bg-teal-600 transition"
                >
                  <Plus size={16} />
                  Add Item
                </button>
              )}
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4">
              {formData.checklistItems.map((item, index) => (
                <div key={index} className="flex flex-col gap-2 p-3 bg-gray-50 rounded-md">
                  <div className="flex gap-2 items-start">
                    <input
                      type="checkbox"
                      checked={item.checked}
                      onChange={(e) =>
                        handleChecklistItemChange(index, "checked", e.target.checked)
                      }
                      disabled={isDisabled}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <input
                        type="text"
                        value={item.item}
                        onChange={(e) =>
                          handleChecklistItemChange(index, "item", e.target.value)
                        }
                        disabled={isDisabled}
                        placeholder="Enter checklist item"
                        className={`w-full p-2 border ${
                          errors[`checklistItem_${index}`]
                            ? "border-red-500"
                            : "border-gray-300"
                        } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                          isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                        }`}
                        required
                      />
                      {errors[`checklistItem_${index}`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors[`checklistItem_${index}`]}
                        </p>
                      )}
                    </div>
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => handleRemoveChecklistItem(index)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  <textarea
                    value={item.remarks || ""}
                    onChange={(e) =>
                      handleChecklistItemChange(index, "remarks", e.target.value)
                    }
                    disabled={isDisabled}
                    placeholder="Remarks (optional)"
                    rows={2}
                    className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                      isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                  />
                </div>
              ))}
              {formData.checklistItems.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">
                  No checklist items. Click "Add Item" to add one.
                </p>
              )}
            </div>
            {errors.checklistItems && (
              <p className="text-red-500 text-sm mt-1">{errors.checklistItems}</p>
            )}
          </div>

          {/* Remarks */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Remarks</label>
            <textarea
              value={formData.remarks}
              onChange={(e) => handleChange("remarks", e.target.value)}
              disabled={isDisabled}
              rows={3}
              className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
              placeholder="Enter additional remarks (optional)"
              readOnly={isReadOnly}
            />
          </div>

          <div className="h-20" />
        </form>
      </div>

      <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
        <button
          type="button"
          className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-[#6b7d96] transition"
          onClick={onClose}
          disabled={checklistsLoading}
        >
          Cancel
        </button>
        {!isReadOnly && (
          <button
            type="submit"
            form="infectionControlChecklistForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
            disabled={checklistsLoading}
          >
            {checklistsLoading ? "Saving..." : mode === "create" ? "Create" : "Update"}
          </button>
        )}
      </div>
    </Modal>
  );
};

export default InfectionControlChecklistForm;

